import React, { useState, useEffect, useRef } from 'react';
import jsQR from 'jsqr';

type ScanMode = 'qrcode' | 'barcode';

interface CameraScannerModalProps {
  show: boolean;
  onClose: () => void;
  onScan: (code: string) => void;
}

const CameraScannerModal: React.FC<CameraScannerModalProps> = ({ show, onClose, onScan }) => {
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [scanMode, setScanMode] = useState<ScanMode>('qrcode');
  const scanModeRef = useRef<ScanMode>('qrcode');
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const scanningRef = useRef(false);
  const animationFrameRef = useRef<number | null>(null);

  // Manter ref sincronizado com o state para acesso dentro do loop de scanning
  useEffect(() => {
    scanModeRef.current = scanMode;
  }, [scanMode]);

  useEffect(() => {
    if (!show) return;

    let mounted = true;
    let activeStream: MediaStream | null = null;
    let barcodeInterval: ReturnType<typeof setInterval> | null = null;

    const onDetected = (data: string) => {
      if (!mounted) return;
      scanningRef.current = false;
      if ('vibrate' in navigator) navigator.vibrate([200]);
      onScan(data);
    };

    const scanFrame = () => {
      if (!scanningRef.current || !mounted || !videoRef.current || !canvasRef.current) return;

      const video = videoRef.current;
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      if (!ctx || !video.videoWidth || !video.videoHeight) {
        animationFrameRef.current = requestAnimationFrame(scanFrame);
        return;
      }

      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

      // Só usar jsQR quando estiver no modo QR Code
      if (scanModeRef.current === 'qrcode') {
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const code = jsQR(imageData.data, imageData.width, imageData.height, {
          inversionAttempts: 'dontInvert',
        });

        if (code?.data) {
          onDetected(code.data);
          return;
        }
      }

      if (scanningRef.current && mounted) {
        animationFrameRef.current = requestAnimationFrame(scanFrame);
      }
    };

    const initCamera = async () => {
      setCameraError(null);
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment' }
        });

        activeStream = stream;

        if (!mounted || !videoRef.current) {
          stream.getTracks().forEach(t => t.stop());
          activeStream = null;
          return;
        }

        videoRef.current.srcObject = stream;
        await videoRef.current.play();
        scanningRef.current = true;
        scanFrame();

        // BarcodeDetector nativo para códigos de barras 1D - só ativa no modo barcode
        if ('BarcodeDetector' in window) {
          barcodeInterval = setInterval(async () => {
            if (!scanningRef.current || !videoRef.current || !mounted) return;
            if (scanModeRef.current !== 'barcode') return;
            try {
              const detector = new (window as any).BarcodeDetector({
                formats: ['code_128', 'code_39', 'ean_13', 'ean_8', 'upc_a', 'upc_e', 'itf', 'codabar']
              });
              const barcodes = await detector.detect(videoRef.current);
              if (barcodes.length > 0 && scanningRef.current && mounted) {
                onDetected(barcodes[0].rawValue);
              }
            } catch { /* silenciar */ }
          }, 300);
        }
      } catch {
        if (mounted) {
          setCameraError('Não foi possível acessar a câmera. Verifique as permissões do navegador.');
        }
      }
    };

    initCamera();

    return () => {
      mounted = false;
      scanningRef.current = false;
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      if (barcodeInterval) clearInterval(barcodeInterval);
      if (activeStream) {
        activeStream.getTracks().forEach(track => track.stop());
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  if (!show) return null;

  return (
    <div className="camera-scanner-overlay" style={{ zIndex: 99999 }} onClick={onClose}>
      <div className="camera-scanner-modal" onClick={(e) => e.stopPropagation()}>
        <div className="camera-scanner-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <i className="fas fa-camera" style={{ color: '#27ae60' }}></i>
            <h4 style={{ margin: 0, fontSize: '1rem', color: '#2c3e50' }}>Escanear Código</h4>
          </div>
          <button
            type="button"
            className="camera-scanner-close"
            onClick={onClose}
          >
            <i className="fas fa-times"></i>
          </button>
        </div>

        {/* Toggle QR Code / Código de Barras */}
        <div className="camera-scanner-mode-toggle">
          <button
            type="button"
            className={`camera-scanner-mode-btn ${scanMode === 'qrcode' ? 'active' : ''}`}
            onClick={() => setScanMode('qrcode')}
          >
            <i className="fas fa-qrcode"></i>
            QR Code
          </button>
          <button
            type="button"
            className={`camera-scanner-mode-btn ${scanMode === 'barcode' ? 'active' : ''}`}
            onClick={() => setScanMode('barcode')}
          >
            <i className="fas fa-barcode"></i>
            Código de Barras
          </button>
        </div>

        <div className="camera-scanner-viewfinder">
          <video
            ref={videoRef}
            playsInline
            muted
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
          <canvas ref={canvasRef} style={{ display: 'none' }} />
          {!cameraError && (
            <div className={`camera-scanner-crosshair ${scanMode === 'barcode' ? 'barcode-mode' : ''}`}>
              <div className="crosshair-corner top-left"></div>
              <div className="crosshair-corner top-right"></div>
              <div className="crosshair-corner bottom-left"></div>
              <div className="crosshair-corner bottom-right"></div>
            </div>
          )}
        </div>

        <div className="camera-scanner-footer">
          {cameraError ? (
            <div style={{
              padding: '12px 16px', background: '#ffebee', borderRadius: 8,
              border: '1px solid #ffcdd2', color: '#c62828', textAlign: 'center',
              display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center'
            }}>
              <i className="fas fa-exclamation-triangle"></i>
              <span style={{ fontSize: '0.85rem' }}>{cameraError}</span>
            </div>
          ) : (
            <p style={{
              textAlign: 'center', color: '#7f8c8d', fontSize: '0.85rem', margin: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6
            }}>
              <i className="fas fa-crosshairs"></i>
              {scanMode === 'qrcode'
                ? 'Aponte a câmera para o QR Code'
                : 'Aponte a câmera para o código de barras'
              }
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default CameraScannerModal;
